import { useState } from 'react';
import type { CSSProperties } from 'react';
import { configuredAssetUrl, getTextFieldInputClassName } from './helpers.ts';

export type ShowPasswordButtonConfig = {
  enabled: boolean;
  defaultHiddenAsset?: string | null;
  enableHiddenHoverAsset?: boolean;
  hoverHiddenAsset?: string | null;
  defaultVisibleAsset?: string | null;
  enableVisibleHoverAsset?: boolean;
  hoverVisibleAsset?: string | null;
  showLabel: string;
  hideLabel: string;
};

type TextModeInputProps = {
  inputId: string;
  describedBy: string;
  hasError: boolean;
  required?: boolean;
  isNumberMode: boolean;
  value: string;
  placeholder: string;
  minLength?: number;
  maxLength?: number;
  inputBorderStyle: string;
  additionInputStyle: CSSProperties | undefined;
  inlineCharacterCount?: string;
  characterCountId?: string;
  showPassword?: ShowPasswordButtonConfig;
  onChange: (value: string) => void;
};

type ShowPasswordIcons = {
  hidden: string;
  hiddenHover?: string;
  visible: string;
  visibleHover?: string;
};

/**
 * The toggle needs both default assets so it can show and hide the value.
 */
function getShowPasswordIcons(
  config: ShowPasswordButtonConfig
): ShowPasswordIcons | undefined {
  const hidden = configuredAssetUrl(config.defaultHiddenAsset);
  const visible = configuredAssetUrl(config.defaultVisibleAsset);
  if (!hidden || !visible) return undefined;

  return {
    hidden,
    visible,
    hiddenHover: config.enableHiddenHoverAsset
      ? configuredAssetUrl(config.hoverHiddenAsset)
      : undefined,
    visibleHover: config.enableVisibleHoverAsset
      ? configuredAssetUrl(config.hoverVisibleAsset)
      : undefined,
  };
}

function ShowPasswordStateIcons({
  state,
  defaultAsset,
  hoverAsset,
}: {
  state: 'hidden' | 'visible';
  defaultAsset: string;
  hoverAsset?: string;
}): JSX.Element {
  return (
    <>
      <img
        alt=""
        src={defaultAsset}
        className={`text-field-show-password-icon-${state}`}
      />
      {hoverAsset ? (
        <>
          <img
            alt=""
            src={hoverAsset}
            className={`text-field-show-password-icon-${state}-hover`}
          />
          <img
            alt=""
            src={hoverAsset}
            className={`text-field-show-password-icon-${state}-focus`}
          />
        </>
      ) : null}
    </>
  );
}

export function TextModeInput({
  inputId,
  describedBy,
  hasError,
  required,
  isNumberMode,
  value,
  placeholder,
  minLength,
  maxLength,
  inputBorderStyle,
  additionInputStyle,
  inlineCharacterCount,
  characterCountId,
  showPassword,
  onChange,
}: TextModeInputProps): JSX.Element {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const passwordEnabled = showPassword?.enabled === true;
  const icons =
    passwordEnabled && showPassword
      ? getShowPasswordIcons(showPassword)
      : undefined;

  return (
    <div
      className={
        icons ? 'text-field-show-password relative w-full' : 'relative w-full'
      }
    >
      <input
        id={inputId}
        type={passwordEnabled && !passwordVisible ? 'password' : 'text'}
        inputMode={isNumberMode ? 'numeric' : undefined}
        value={value}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        aria-invalid={hasError ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={getTextFieldInputClassName({
          inputBorderStyle,
          hasError,
        })}
        style={additionInputStyle}
        onChange={(event) => onChange(event.target.value)}
      />
      {inlineCharacterCount ? (
        <span
          className="text-field-inline-character-count"
          id={characterCountId}
        >
          {inlineCharacterCount}
        </span>
      ) : null}
      {icons && showPassword ? (
        <button
          type="button"
          className="text-field-show-password-button"
          aria-label={
            passwordVisible ? showPassword.hideLabel : showPassword.showLabel
          }
          aria-pressed={passwordVisible}
          onClick={() => setPasswordVisible((current) => !current)}
        >
          <ShowPasswordStateIcons
            state="hidden"
            defaultAsset={icons.hidden}
            hoverAsset={icons.hiddenHover}
          />
          <ShowPasswordStateIcons
            state="visible"
            defaultAsset={icons.visible}
            hoverAsset={icons.visibleHover}
          />
        </button>
      ) : null}
    </div>
  );
}
